import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fetchWithTimeout, isTimeoutError, isNetworkError, categorizeError } from '../utils/networkError';
import ApiErrorAlert from '../components/ApiErrorAlert';
import { formatShortDate, formatTime as formatTimeUtil } from '../utils/dateFormat';
import DoctorSidebar from '../components/DoctorSidebar';
import AnalyticsChart from '../components/AnalyticsChart';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// Loading skeleton component
const StatCardSkeleton = () => (
  <div className="glass border border-light-border dark:border-border rounded-2xl p-6 animate-pulse">
    <div className="h-3 bg-slate-200 dark:bg-silver/20 rounded w-32 mb-4"></div>
    <div className="h-8 bg-slate-200 dark:bg-silver/20 rounded w-12"></div>
  </div>
);

// Loading skeleton for appointments section
const AppointmentsSkeleton = () => (
  <div className="animate-pulse space-y-4">
    {[1, 2, 3].map((i) => (
      <div key={i} className="border border-light-border dark:border-border rounded-xl p-4">
        <div className="flex justify-between items-start">
          <div className="space-y-2">
            <div className="h-4 bg-slate-200 dark:bg-silver/20 rounded w-32"></div>
            <div className="h-3 bg-slate-200 dark:bg-silver/20 rounded w-24"></div>
          </div>
          <div className="h-6 bg-slate-200 dark:bg-silver/20 rounded w-20"></div>
        </div>
      </div>
    ))}
  </div>
);

// Stat card component
const StatCard = ({ label, value, isLoading }) => {
  if (isLoading) {
    return <StatCardSkeleton />;
  }

  return (
    <div className="glass border border-light-border dark:border-border rounded-2xl p-6">
      <p className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2">
        {label}
      </p>
      <p className="font-display text-3xl italic">{value}</p>
    </div>
  );
};

const DoctorDashboard = () => {
  const { user, token, loading: authLoading, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  // Dashboard data state
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [dataError, setDataError] = useState(null);

  // Cancellation state
  const [cancellingAppointmentId, setCancellingAppointmentId] = useState(null);
  const [cancelError, setCancelError] = useState(null);

  // Export state
  const [exporting, setExporting] = useState(false);

  // Filter state for exports
  const [exportDateFrom, setExportDateFrom] = useState('');
  const [exportDateTo, setExportDateTo] = useState('');
  const [showExportFilters, setShowExportFilters] = useState(false);

  // Date range filter state for link-in-bio analytics
  const [analyticsDateRange, setAnalyticsDateRange] = useState('30'); // 7, 30, 90, or 'custom'
  const [analyticsDateFrom, setAnalyticsDateFrom] = useState('');
  const [analyticsDateTo, setAnalyticsDateTo] = useState('');

  // Analytics time-series data
  const [analyticsData, setAnalyticsData] = useState(null);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);

  useEffect(() => {
    // Redirect if not authenticated or not a doctor
    if (!authLoading && (!isAuthenticated || user?.role !== 'doctor')) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  // Store the error object for proper categorization
  const [apiError, setApiError] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);

  // Fetch dashboard data with timeout handling
  const fetchDashboardData = useCallback(async () => {
    if (!isAuthenticated || user?.role !== 'doctor' || !token) {
      return;
    }

    setIsLoadingData(true);
    setDataError(null);
    setApiError(null);

    try {
      // Build query params for date range filtering
      const params = new URLSearchParams();
      if (analyticsDateRange === '7days') {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 7);
        params.set('dateFrom', startDate.toISOString().split('T')[0]);
        params.set('dateTo', endDate.toISOString().split('T')[0]);
      } else if (analyticsDateRange === '30days') {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 30);
        params.set('dateFrom', startDate.toISOString().split('T')[0]);
        params.set('dateTo', endDate.toISOString().split('T')[0]);
      } else if (analyticsDateRange === 'custom' && analyticsDateFrom && analyticsDateTo) {
        params.set('dateFrom', analyticsDateFrom);
        params.set('dateTo', analyticsDateTo);
      }

      const url = params.toString()
        ? `${API_URL}/doctors/me/dashboard?${params.toString()}`
        : `${API_URL}/doctors/me/dashboard`;

      const response = await fetchWithTimeout(
        url,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        },
        15000 // 15 second timeout
      );

      if (!response.ok) {
        throw new Error('Failed to fetch dashboard data');
      }

      const data = await response.json();
      setDashboardData(data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      // Store the full error object for categorization
      setApiError(error);

      // Set appropriate error message based on error type
      if (isTimeoutError(error)) {
        setDataError('Request timed out. The server is taking too long to respond.');
      } else if (isNetworkError(error)) {
        setDataError('Unable to connect to the server. Please check your connection.');
      } else {
        setDataError(error.message);
      }
    } finally {
      setIsLoadingData(false);
      setIsRetrying(false);
    }
  }, [isAuthenticated, user, token, analyticsDateRange, analyticsDateFrom, analyticsDateTo]);

  // Fetch analytics time-series data
  const fetchAnalyticsData = useCallback(async () => {
    if (!isAuthenticated || user?.role !== 'doctor' || !token) {
      return;
    }

    setIsLoadingAnalytics(true);

    try {
      // Build query parameters
      let queryParams = '';
      if (analyticsDateRange === 'custom' && analyticsDateFrom && analyticsDateTo) {
        queryParams = `?from=${analyticsDateFrom}&to=${analyticsDateTo}`;
      } else {
        queryParams = `?days=${analyticsDateRange}`;
      }

      const response = await fetchWithTimeout(
        `${API_URL}/doctors/me/analytics${queryParams}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
        10000
      );

      if (!response.ok) {
        throw new Error('Failed to fetch analytics data');
      }

      const data = await response.json();
      setAnalyticsData(data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      // Fail silently - analytics is a nice-to-have feature
    } finally {
      setIsLoadingAnalytics(false);
    }
  }, [isAuthenticated, user, token, analyticsDateRange, analyticsDateFrom, analyticsDateTo]);

  // Retry handler
  const handleRetry = useCallback(() => {
    setIsRetrying(true);
    fetchDashboardData();
  }, [fetchDashboardData]);

  useEffect(() => {
    if (isAuthenticated && user?.role === 'doctor' && token) {
      fetchDashboardData();
      fetchAnalyticsData();
    }
  }, [isAuthenticated, user, token, fetchDashboardData, fetchAnalyticsData]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Export appointments to CSV - respects current filters
  const handleExportCSV = async () => {
    setExporting(true);
    try {
      // Build export query string with filters (respects current filter settings)
      const params = new URLSearchParams({ sortOrder: 'desc' });

      // Add date filters if set - export respects current filters
      if (exportDateFrom) {
        params.append('from', `${exportDateFrom}T00:00:00.000Z`);
      }
      if (exportDateTo) {
        params.append('to', `${exportDateTo}T23:59:59.999Z`);
      }

      console.log('[EXPORT] Exporting with filters:', { dateFrom: exportDateFrom, dateTo: exportDateTo });

      const response = await fetchWithTimeout(
        `${API_URL}/appointments/export/csv?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        },
        30000 // 30 second timeout for export
      );

      if (!response.ok) {
        throw new Error('Failed to export appointments');
      }

      // Get the CSV content as text
      const csvContent = await response.text();

      // Create a Blob and download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'appointments_export.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('[EXPORT] CSV downloaded successfully');
    } catch (error) {
      console.error('Export error:', error);
      setCancelError('Failed to export appointments. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Cancel appointment handler
  const handleCancelAppointment = async (appointmentId) => {
    if (!window.confirm('Are you sure you want to cancel this appointment? The time slot will become available for other patients.')) {
      return;
    }

    setCancellingAppointmentId(appointmentId);
    setCancelError(null);

    try {
      const response = await fetchWithTimeout(
        `${API_URL}/appointments/${appointmentId}/cancel`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ reason: 'Cancelled by doctor' })
        },
        10000
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to cancel appointment');
      }

      // Refresh dashboard data to show updated appointments
      fetchDashboardData();
    } catch (error) {
      console.error('Cancel appointment error:', error);
      setCancelError(error.message);
    } finally {
      setCancellingAppointmentId(null);
    }
  };

  // Show full page loading spinner during authentication check
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-5rem)]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-light-text dark:border-pure-white mx-auto mb-4"></div>
          <p className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60">
            Loading...
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || user?.role !== 'doctor') {
    return null;
  }

  // Format appointment time using locale-aware utilities
  const formatTime = (dateString) => formatTimeUtil(dateString);
  const formatDate = (dateString) => formatShortDate(dateString);

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-500/20 text-green-400';
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-400';
      case 'completed':
        return 'bg-blue-500/20 text-blue-400';
      default:
        return 'bg-slate-200 dark:bg-silver/20 text-slate-500 dark:text-silver/60';
    }
  };

  return (
    <div className="flex">
      <DoctorSidebar />
      <div className="flex-1 min-h-[calc(100vh-5rem)] px-4 py-8">
        <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="flex justify-between items-start mb-8">
          <div className="flex-1 min-w-0 mr-4">
            <h1 className="font-display text-4xl italic tracking-tighter leading-tight mb-2">
              Doctor Dashboard
            </h1>
            <p
              className="font-body text-light-text-secondary dark:text-silver/60 truncate"
              title={`Welcome back, Dr. ${user?.firstName} ${user?.lastName}`}
            >
              Welcome back, Dr. {user?.firstName} {user?.lastName}
            </p>
          </div>
          <button
            onClick={handleLogout}
            className="px-4 py-2 border border-light-border dark:border-border rounded-xl font-mono text-xs uppercase tracking-widest hover:border-white/20 transition-smooth"
          >
            Logout
          </button>
        </div>

        {/* Error message with retry capability */}
        {apiError && (
          <ApiErrorAlert
            error={apiError}
            onRetry={handleRetry}
            isRetrying={isRetrying}
          />
        )}

        {/* Link-in-Bio URL Section */}
        {dashboardData?.doctorSlug && (
          <div className="glass border border-light-border dark:border-border rounded-2xl p-6 mb-8">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2">
                  Your Link-in-Bio URL
                </h2>
                <a
                  href={`${window.location.origin}/doctor/${dashboardData.doctorSlug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-lg text-light-text dark:text-pure-white hover:text-light-text-secondary dark:hover:text-silver/80 transition-smooth inline-flex items-center gap-2 break-all"
                >
                  {window.location.origin}/doctor/{dashboardData.doctorSlug}
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
                <p className="font-body text-sm text-light-text-secondary dark:text-silver/60 mt-2">
                  Share this link on social media, email signatures, or anywhere you want to promote your services
                </p>
              </div>
              <button
                onClick={() => {
                  const url = `${window.location.origin}/doctor/${dashboardData.doctorSlug}`;
                  navigator.clipboard.writeText(url);
                  // Could add a toast notification here
                  alert('URL copied to clipboard!');
                }}
                className="px-4 py-2 border border-light-border dark:border-border rounded-xl font-mono text-xs uppercase tracking-widest hover:border-light-text/40 dark:hover:border-silver/40 transition-smooth flex-shrink-0"
                title="Copy to clipboard"
              >
                Copy
              </button>
            </div>
          </div>
        )}

        {/* Quick Stats - Shows loading skeletons or real data */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <StatCard
            label="Today's Appointments"
            value={dashboardData?.stats?.todayAppointments ?? 0}
            isLoading={isLoadingData}
          />
          <StatCard
            label="This Week"
            value={dashboardData?.stats?.weekAppointments ?? 0}
            isLoading={isLoadingData}
          />
          <StatCard
            label="Total Patients"
            value={dashboardData?.stats?.totalPatients ?? 0}
            isLoading={isLoadingData}
          />
          <StatCard
            label="Average Rating"
            value={dashboardData?.stats?.averageRating > 0
              ? `${dashboardData.stats.averageRating.toFixed(1)} ★`
              : '-'}
            isLoading={isLoadingData}
          />
          <StatCard
            label="Profile Views"
            value={dashboardData?.stats?.pageViewCount ?? 0}
            isLoading={isLoadingData}
          />
        </div>

        {/* Link-in-Bio Analytics Section */}
        <div className="glass border border-light-border dark:border-border rounded-2xl p-6 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60">
              Link-in-Bio Analytics
            </h2>

            {/* Date Range Filter */}
            <div className="flex items-center gap-3">
              <select
                value={analyticsDateRange}
                onChange={(e) => setAnalyticsDateRange(e.target.value)}
                className="px-3 py-2 bg-white dark:bg-charcoal border border-light-border dark:border-border rounded-xl font-mono text-xs uppercase tracking-widest focus:outline-none focus:ring-2 focus:ring-light-text/20 dark:focus:ring-silver/20"
              >
                <option value="7">Last 7 Days</option>
                <option value="30">Last 30 Days</option>
                <option value="90">Last 90 Days</option>
                <option value="custom">Custom Range</option>
              </select>

              {analyticsDateRange === 'custom' && (
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={analyticsDateFrom}
                    onChange={(e) => setAnalyticsDateFrom(e.target.value)}
                    className="px-3 py-2 bg-white dark:bg-charcoal border border-light-border dark:border-border rounded-xl font-mono text-xs focus:outline-none focus:ring-2 focus:ring-light-text/20 dark:focus:ring-silver/20"
                  />
                  <span className="text-light-text-secondary dark:text-silver/60">to</span>
                  <input
                    type="date"
                    value={analyticsDateTo}
                    onChange={(e) => setAnalyticsDateTo(e.target.value)}
                    className="px-3 py-2 bg-white dark:bg-charcoal border border-light-border dark:border-border rounded-xl font-mono text-xs focus:outline-none focus:ring-2 focus:ring-light-text/20 dark:focus:ring-silver/20"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard
              label="Total Page Views"
              value={dashboardData?.stats?.pageViewCount ?? 0}
              isLoading={isLoadingData}
            />
            <StatCard
              label="Unique Visitors"
              value={dashboardData?.stats?.uniqueVisitorCount ?? 0}
              isLoading={isLoadingData}
            />
            <StatCard
              label="Bookings from Link"
              value={dashboardData?.stats?.linkInBioBookings ?? 0}
              isLoading={isLoadingData}
            />
            <StatCard
              label="Conversion Rate"
              value={dashboardData?.stats?.linkInBioConversionRate > 0
                ? `${dashboardData.stats.linkInBioConversionRate.toFixed(2)}%`
                : '0.00%'}
              isLoading={isLoadingData}
            />
          </div>

          {/* Time-Series Chart */}
          <div>
            <h3 className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-4">
              Trend Over Time
            </h3>
            {isLoadingAnalytics ? (
              <div className="h-64 flex items-center justify-center">
                <div className="animate-pulse text-slate-400 dark:text-silver/40">
                  Loading chart...
                </div>
              </div>
            ) : analyticsData?.timeSeries ? (
              <AnalyticsChart data={analyticsData.timeSeries} />
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-400 dark:text-silver/40">
                No analytics data available
              </div>
            )}
          </div>
        </div>

        {/* Subscription Usage */}
        <div className="glass border border-light-border dark:border-border rounded-2xl p-6 mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60">
              Monthly Usage
            </h2>
            {dashboardData?.subscription && (
              <span className="px-3 py-1 bg-gradient-to-r from-silver/20 to-silver/10 rounded-full font-mono text-xs uppercase tracking-wider">
                {dashboardData.subscription.planTier} Plan
              </span>
            )}
          </div>

          {isLoadingData ? (
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-slate-200 dark:bg-silver/20 rounded w-full"></div>
              <div className="h-2 bg-slate-100 dark:bg-silver/10 rounded-full"></div>
            </div>
          ) : dashboardData?.subscription ? (
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="font-body text-light-text-secondary dark:text-silver/80">
                  Appointments this month
                </span>
                <span className="font-mono text-lg">
                  <span className="text-light-text dark:text-pure-white">{dashboardData.subscription.appointmentsUsed}</span>
                  <span className="text-slate-400 dark:text-silver/40"> / {dashboardData.subscription.appointmentsLimit}</span>
                </span>
              </div>

              {/* Progress bar */}
              <div className="relative h-2 bg-slate-100 dark:bg-charcoal rounded-full overflow-hidden">
                <div
                  className={`absolute top-0 left-0 h-full rounded-full transition-all duration-500 ${
                    dashboardData.subscription.percentageUsed >= 90
                      ? 'bg-gradient-to-r from-red-500 to-red-400'
                      : dashboardData.subscription.percentageUsed >= 70
                      ? 'bg-gradient-to-r from-yellow-500 to-yellow-400'
                      : 'bg-gradient-to-r from-silver to-pure-white'
                  }`}
                  style={{ width: `${Math.min(dashboardData.subscription.percentageUsed, 100)}%` }}
                />
              </div>

              <div className="flex justify-between items-center mt-2">
                <span className="font-mono text-xs text-slate-400 dark:text-silver/40">
                  {dashboardData.subscription.percentageUsed}% used
                </span>
                {dashboardData.subscription.percentageUsed >= 80 && (
                  <Link
                    to="/doctor/subscription"
                    className="font-mono text-xs text-yellow-400 hover:text-yellow-300 transition-smooth"
                  >
                    Upgrade Plan →
                  </Link>
                )}
              </div>
            </div>
          ) : (
            <p className="text-light-text-secondary dark:text-silver/60">No subscription found</p>
          )}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Upcoming Appointments */}
          <div className="lg:col-span-2 glass border border-light-border dark:border-border rounded-2xl p-6">
            <div className="flex flex-col gap-4 mb-4">
              <div className="flex justify-between items-center">
                <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60">
                  Upcoming Appointments
                </h2>
                <div className="flex items-center gap-2">
                  {/* Filter Toggle Button */}
                  <button
                    onClick={() => setShowExportFilters(!showExportFilters)}
                    className={`flex items-center gap-2 px-3 py-1.5 border rounded-lg font-mono text-xs uppercase tracking-widest transition-smooth ${
                      showExportFilters || exportDateFrom || exportDateTo
                        ? 'border-light-text/40 dark:border-silver/40 bg-slate-100 dark:bg-silver/5'
                        : 'border-light-border dark:border-border hover:border-light-text/40 dark:hover:border-silver/40'
                    }`}
                    data-testid="export-filter-toggle"
                    title="Filter appointments for export"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                    {(exportDateFrom || exportDateTo) ? 'Filtered' : 'Filter'}
                  </button>

                  {/* Export to CSV Button */}
                  <button
                    onClick={handleExportCSV}
                    disabled={exporting || isLoadingData}
                    className="flex items-center gap-2 px-3 py-1.5 border border-light-border dark:border-border rounded-lg font-mono text-xs uppercase tracking-widest hover:border-light-text/40 dark:hover:border-silver/40 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="export-csv-button"
                    title={(exportDateFrom || exportDateTo) ? 'Export filtered appointments to CSV' : 'Export all appointments to CSV'}
                  >
                    {exporting ? (
                      <>
                        <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Exporting...
                      </>
                    ) : (
                      <>
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        {(exportDateFrom || exportDateTo) ? 'Export Filtered' : 'Export CSV'}
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Export Date Filter Panel */}
              {showExportFilters && (
                <div className="flex flex-wrap items-center gap-4 p-4 bg-slate-100 dark:bg-charcoal/50 border border-light-border dark:border-border rounded-xl" data-testid="export-filter-panel">
                  <div className="flex items-center gap-2">
                    <label htmlFor="export-date-from" className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60">
                      From:
                    </label>
                    <input
                      id="export-date-from"
                      type="date"
                      value={exportDateFrom}
                      onChange={(e) => setExportDateFrom(e.target.value)}
                      className="bg-light-bg dark:bg-obsidian border border-light-border dark:border-border rounded-lg px-3 py-2 font-mono text-sm text-light-text dark:text-pure-white focus:outline-none focus:border-light-text/40 dark:focus:border-silver/40 transition-colors"
                      data-testid="export-date-from"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label htmlFor="export-date-to" className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60">
                      To:
                    </label>
                    <input
                      id="export-date-to"
                      type="date"
                      value={exportDateTo}
                      onChange={(e) => setExportDateTo(e.target.value)}
                      className="bg-light-bg dark:bg-obsidian border border-light-border dark:border-border rounded-lg px-3 py-2 font-mono text-sm text-light-text dark:text-pure-white focus:outline-none focus:border-light-text/40 dark:focus:border-silver/40 transition-colors"
                      data-testid="export-date-to"
                    />
                  </div>
                  {/* Clear Filters Button */}
                  {(exportDateFrom || exportDateTo) && (
                    <button
                      onClick={() => {
                        setExportDateFrom('');
                        setExportDateTo('');
                      }}
                      className="text-light-text-secondary dark:text-silver/60 hover:text-light-text dark:hover:text-silver text-xs font-mono uppercase tracking-widest transition-colors px-2 py-1"
                      data-testid="clear-export-filters"
                    >
                      Clear
                    </button>
                  )}
                  {/* Show filter status */}
                  {(exportDateFrom || exportDateTo) && (
                    <span className="text-slate-400 dark:text-silver/40 text-xs font-mono ml-auto">
                      Export will include only filtered appointments
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Cancel error message */}
            {cancelError && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl">
                <p className="text-red-400 font-mono text-xs">{cancelError}</p>
              </div>
            )}

            {isLoadingData ? (
              <AppointmentsSkeleton />
            ) : dashboardData?.upcomingAppointments?.length > 0 ? (
              <div className="space-y-3">
                {dashboardData.upcomingAppointments.map((apt) => (
                  <div
                    key={apt.id}
                    className="border border-light-border dark:border-border rounded-xl p-4 hover:border-white/20 transition-smooth"
                  >
                    <div className="flex justify-between items-start">
                      <Link
                        to={`/doctor/appointments/${apt.id}`}
                        className="flex-1 hover:opacity-80 transition-smooth"
                      >
                        <p className="font-body font-medium">
                          {apt.patientName || 'Guest Patient'}
                        </p>
                        <p className="font-mono text-xs text-light-text-secondary dark:text-silver/60 mt-1">
                          {apt.serviceName} • {apt.durationMinutes} min
                        </p>
                        <p className="font-mono text-xs text-slate-400 dark:text-silver/40 mt-1">
                          {formatDate(apt.startTime)} at {formatTime(apt.startTime)}
                        </p>
                      </Link>
                      <div className="flex items-start gap-2">
                        <span className={`px-2 py-1 rounded-lg font-mono text-xs uppercase ${getStatusColor(apt.status)}`}>
                          {apt.status}
                        </span>
                        {(apt.status === 'pending' || apt.status === 'confirmed') && (
                          <button
                            onClick={() => handleCancelAppointment(apt.id)}
                            disabled={cancellingAppointmentId === apt.id}
                            className="px-2 py-1 bg-red-500/10 border border-red-500/30 rounded-lg font-mono text-xs uppercase text-red-400 hover:bg-red-500/20 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {cancellingAppointmentId === apt.id ? 'Cancelling...' : 'Cancel'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="font-body text-light-text-secondary dark:text-silver/60 mb-4">
                  No upcoming appointments
                </p>
                <p className="font-body text-sm text-slate-400 dark:text-silver/40">
                  Set up your availability to start receiving bookings
                </p>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="glass border border-light-border dark:border-border rounded-2xl p-6">
            <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-4">
              Quick Actions
            </h2>
            <div className="space-y-3">
              <Link
                to="/doctor/profile"
                className="block w-full p-4 border border-light-border dark:border-border rounded-xl hover:border-white/20 transition-smooth"
              >
                <p className="font-mono text-sm uppercase tracking-wider">
                  Edit Profile
                </p>
                <p className="font-body text-xs text-light-text-secondary dark:text-silver/60 mt-1">
                  Update your bio and specialties
                </p>
              </Link>
              <Link
                to="/doctor/services"
                className="block w-full p-4 border border-light-border dark:border-border rounded-xl hover:border-white/20 transition-smooth"
              >
                <p className="font-mono text-sm uppercase tracking-wider">
                  Manage Services
                </p>
                <p className="font-body text-xs text-light-text-secondary dark:text-silver/60 mt-1">
                  Add or edit your services
                </p>
              </Link>
              <Link
                to="/doctor/availability"
                className="block w-full p-4 border border-light-border dark:border-border rounded-xl hover:border-white/20 transition-smooth"
              >
                <p className="font-mono text-sm uppercase tracking-wider">
                  Set Availability
                </p>
                <p className="font-body text-xs text-light-text-secondary dark:text-silver/60 mt-1">
                  Configure your working hours
                </p>
              </Link>
              <Link
                to="/doctor/locations"
                className="block w-full p-4 border border-light-border dark:border-border rounded-xl hover:border-white/20 transition-smooth"
              >
                <p className="font-mono text-sm uppercase tracking-wider">
                  Manage Locations
                </p>
                <p className="font-body text-xs text-light-text-secondary dark:text-silver/60 mt-1">
                  Add or edit your practice locations
                </p>
              </Link>
              <Link
                to="/doctor/embed"
                className="block w-full p-4 border border-light-border dark:border-border rounded-xl hover:border-white/20 transition-smooth"
              >
                <p className="font-mono text-sm uppercase tracking-wider">
                  Get Embed Widget
                </p>
                <p className="font-body text-xs text-light-text-secondary dark:text-silver/60 mt-1">
                  Add booking to your website
                </p>
              </Link>
              <Link
                to="/doctor/link-in-bio"
                className="block w-full p-4 border border-light-border dark:border-border rounded-xl hover:border-white/20 transition-smooth"
              >
                <p className="font-mono text-sm uppercase tracking-wider">
                  Link-in-Bio Settings
                </p>
                <p className="font-body text-xs text-light-text-secondary dark:text-silver/60 mt-1">
                  Create a custom URL for your profile
                </p>
              </Link>
              <Link
                to="/doctor/payments"
                className="block w-full p-4 border border-light-border dark:border-border rounded-xl hover:border-white/20 transition-smooth"
              >
                <p className="font-mono text-sm uppercase tracking-wider">
                  Payment Settings
                </p>
                <p className="font-body text-xs text-light-text-secondary dark:text-silver/60 mt-1">
                  Connect Stripe and manage payouts
                </p>
              </Link>
              <Link
                to="/doctor/cancellation-policy"
                className="block w-full p-4 border border-light-border dark:border-border rounded-xl hover:border-white/20 transition-smooth"
              >
                <p className="font-mono text-sm uppercase tracking-wider">
                  Cancellation Policy
                </p>
                <p className="font-body text-xs text-light-text-secondary dark:text-silver/60 mt-1">
                  Configure refund and cancellation rules
                </p>
              </Link>
              <Link
                to="/doctor/settings"
                className="block w-full p-4 border border-light-border dark:border-border rounded-xl hover:border-white/20 transition-smooth"
              >
                <p className="font-mono text-sm uppercase tracking-wider">
                  Account Settings
                </p>
                <p className="font-body text-xs text-light-text-secondary dark:text-silver/60 mt-1">
                  Manage account and preferences
                </p>
              </Link>
            </div>
          </div>
        </div>

        {/* Account Info */}
        <div className="mt-8 glass border border-light-border dark:border-border rounded-2xl p-6">
          <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-4">
            Account Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-slate-400 dark:text-silver/40 mb-1">
                Email
              </p>
              <p className="font-body">{user?.email}</p>
            </div>
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-slate-400 dark:text-silver/40 mb-1">
                Role
              </p>
              <p className="font-body capitalize">{user?.role}</p>
            </div>
            <div className="min-w-0">
              <p className="font-mono text-xs uppercase tracking-widest text-slate-400 dark:text-silver/40 mb-1">
                Name
              </p>
              <p
                className="font-body truncate"
                title={`${user?.firstName} ${user?.lastName}`}
              >
                {user?.firstName} {user?.lastName}
              </p>
            </div>
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-slate-400 dark:text-silver/40 mb-1">
                User ID
              </p>
              <p className="font-body font-mono text-xs">{user?.id}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};

export default DoctorDashboard;
